import { useQueries } from "@tanstack/react-query";
import type { RankOption, RoleOption } from "gsintegrations";
import { rankRepository } from "../repositories/rankRepository";
import { roleRepository } from "../repositories/roleRepository";

// Query keys
export const onboardingQueryKeys = {
  ranks: ["rank"] as const,
  roles: ["role"] as const,
};

interface UseOnboardingMediumQueriesOptions {
  enabled?: boolean;
}

export const useOnboardingMediumQueries = (
  options: UseOnboardingMediumQueriesOptions = {},
) => {
  const { enabled = true } = options;

  const queries = useQueries({
    queries: [
      {
        queryKey: onboardingQueryKeys.ranks,
        queryFn: () => rankRepository.getRanks(),
        enabled,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
            refetchOnMount: "always",
      },
      {
        queryKey: onboardingQueryKeys.roles,
        queryFn: () => roleRepository.getOnboardingRoles(),
        enabled,
        staleTime: 1000 * 60 * 5,
        gcTime: 60 * 60 * 1000,
            refetchOnMount: "always",
      },
    ],
  });

  const [ranksQuery, rolesQuery] = queries as [
    {
      data?: RankOption[];
      isLoading: boolean;
      isError: boolean;
      error: unknown;
      refetch: () => Promise<unknown>;
      isSuccess: boolean;
    },
    {
      data?: RoleOption[];
      isLoading: boolean;
      isError: boolean;
      error: unknown;
      refetch: () => Promise<unknown>;
      isSuccess: boolean;
    },
  ];

  return {
    ranks: ranksQuery.data ?? [],
    roles: rolesQuery.data ?? [],

    isRanksLoading: ranksQuery.isLoading,
    isRolesLoading: rolesQuery.isLoading,
    isAnyLoading: queries.some((q) => q.isLoading),

    ranksError: ranksQuery.error,
    rolesError: rolesQuery.error,
    hasAnyError: queries.some((q) => q.error),

    refetchRanks: ranksQuery.refetch,
    refetchRoles: rolesQuery.refetch,
    refetchAll: () => queries.forEach((q) => q.refetch && q.refetch()),

    isSuccess: queries.every((q) => q.isSuccess),
    isError: queries.some((q) => q.isError),
  };
};
